import React, {useState, useEffect} from 'react';
import wishlistIconButtonStyle from './WishlistIconButton.scss';
import {Button, StatesButton, StatesButtonStates} from '@wix/wix-ui-tpa/cssVars';
import {LikeHeart, LikeHeartFill} from '@wix/wix-ui-icons-common/on-stage';
import classNames from 'classnames';
import {ProvidedGlobalProps, withGlobalProps} from '../../../providers/globalPropsProvider';
import {
  IProvidedTranslationProps,
  withTranslations,
} from '@wix/wixstores-client-common-components/dist/es/src/outOfIframes/translations';
import {ActionStatus} from '@wix/wixstores-client-core/dist/es/src/constants';
import {ConditionalRender} from '../../ConditionalRender/ConditionalRender';
import {classes as wishlistButtonStyles} from './WishlistButton.st.css';
import {SPECS} from '../../../specs';
import {useExperiments} from '@wix/yoshi-flow-editor';

export enum Mode {
  INLINE,
  FLOATING,
  WITH_SLOT,
}

export const WishlistButtonDataHook = {
  Root: 'wishlist-button',
  StatesButton: 'wishlist-states-button',
  Icon: 'wishlist-button-icon',
  IconContainer: 'wishlist-button-icon-container',
};

export interface WishlistButtonProps extends ProvidedGlobalProps, IProvidedTranslationProps {
  mode: Mode;
}

const WishlistButtonComponent: React.FC<WishlistButtonProps> = ({mode, globals, t}) => {
  const {
    productWasAddedToWishlist,
    shouldShowSubscribeButton,
    handleWishlistButtonClick,
    wishlistActionStatus,
    resetWishlistStatus,
  } = globals;
  const {experiments} = useExperiments();
  const [wasAddedToWishlist, setWasAddedToWishlist] = useState(false);

  useEffect(() => {
    setWasAddedToWishlist(productWasAddedToWishlist);
  }, [productWasAddedToWishlist]);

  const isFloatingMode = mode === Mode.FLOATING;
  const isWithSlotMode = mode === Mode.WITH_SLOT;

  const onWishlistButtonClick = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    handleWishlistButtonClick();
  };

  const iconButtonAriaLabel = wasAddedToWishlist ? t('sr.wishlist.remove.button') : t('sr.wishlist.button');

  const ribbonsInProductPageEnabled = experiments.enabled(SPECS.ShowMultiRibbonsInProductPage);
  const floatingStylableClass = ribbonsInProductPageEnabled
    ? wishlistButtonStyles.wishlistButtonFloatingMode
    : wishlistButtonStyles.wishlistButtonFloatingModeOld;

  const iconButton = () => {
    const buttonClasses = classNames('wishlistButton', wishlistIconButtonStyle.wishlistIconButton, {
      [wishlistButtonStyles.wishlistButton]: !isFloatingMode,
      [floatingStylableClass]: isFloatingMode,
      [wishlistButtonStyles.wishlistButtonWithSlotMode]: isWithSlotMode,
      [wishlistIconButtonStyle.floatingMode]: isFloatingMode,
      [wishlistIconButtonStyle.withSubscribeButton]: shouldShowSubscribeButton,
      [wishlistIconButtonStyle.withoutSubscribeButton]: !shouldShowSubscribeButton,
    });

    return (
      <Button
        type="button"
        onClick={onWishlistButtonClick}
        className={buttonClasses}
        data-hook={WishlistButtonDataHook.Root}
        aria-label={iconButtonAriaLabel}
        aria-pressed={wasAddedToWishlist}>
        <i className={wishlistIconButtonStyle.iconContainer} data-hook={WishlistButtonDataHook.IconContainer}>
          {wasAddedToWishlist ? (
            <LikeHeartFill
              className={classNames(wishlistIconButtonStyle.heartIcon, wishlistIconButtonStyle.filled)}
              data-hook={WishlistButtonDataHook.Icon}
            />
          ) : (
            <LikeHeart
              className={classNames(wishlistIconButtonStyle.heartIcon, wishlistIconButtonStyle.empty)}
              data-hook={WishlistButtonDataHook.Icon}
            />
          )}
        </i>
      </Button>
    );
  };

  const renderIconButton = () => {
    if (shouldShowSubscribeButton || isFloatingMode) {
      return iconButton();
    } else {
      return <ConditionalRender by="shouldRenderWishlistIconButton">{iconButton()}</ConditionalRender>;
    }
  };

  const fullWidthButton = () => {
    const caption = wasAddedToWishlist ? t('wishlist.remove.button') : t('wishlist.button');

    return (
      <StatesButton
        state={wishlistActionStatus === ActionStatus.SUCCESSFUL ? StatesButtonStates.SUCCESS : StatesButtonStates.IDLE}
        idleContent={caption}
        onNotificationEnd={() => resetWishlistStatus()}
        onClick={onWishlistButtonClick}
        className={wishlistButtonStyles.wishlistFullWidthButton}
        disabled={false}
        fullWidth
        data-hook={WishlistButtonDataHook.StatesButton}
      />
    );
  };

  const renderFullWidthButton = () => {
    if (!shouldShowSubscribeButton && !isFloatingMode) {
      return <ConditionalRender by="shouldRenderWishlistFullWidthButton">{fullWidthButton()}</ConditionalRender>;
    }
  };

  return (
    <>
      {renderIconButton()}
      {renderFullWidthButton()}
    </>
  );
};

export const WishlistButton = withGlobalProps(withTranslations('globals.texts')(WishlistButtonComponent));
